import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Mail, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REQUEST_TIMEOUT_MS = 15000;
const STATUS_RESET_MS = 6000;
// Internal city codes — sent to backend unchanged for consistent lead data.
const CITY_CODES = ['Sydney', 'Brisbane', 'Perth', 'Adelaide', 'Other'];
const INITIAL_FORM = { name: '', email: '', city: 'Sydney', message: '' };

const CTA = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const cityLabels = t('cta.cityOptions', { returnObjects: true });

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setErrorMsg('');
    try {
      const { data } = await axios.post(`${API}/contact`, form, { timeout: REQUEST_TIMEOUT_MS });
      if (data && data.ok) {
        setStatus('success');
        setForm(INITIAL_FORM);
        setTimeout(() => setStatus('idle'), STATUS_RESET_MS);
      } else {
        throw new Error('Unexpected response');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || err?.message || 'Could not send. Please try again.');
      setTimeout(() => setStatus('idle'), STATUS_RESET_MS);
    }
  };

  return (
    <section id="contact" className="relative py-28 md:py-36 border-t border-white/5 overflow-hidden">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] dashed-circle opacity-30 pointer-events-none" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] dashed-circle opacity-20 pointer-events-none" />

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#5FE0C8] mb-5">{t('cta.eyebrow')}</div>
            <h2 className="font-display text-[48px] md:text-[80px] leading-[0.95] tracking-tight">
              {t('cta.heading1')} <span className="font-serif-i text-[#5FE0C8]">{t('cta.heading2')}</span>
            </h2>
            <p className="mt-6 text-[16px] text-[#9A9A8E] leading-relaxed max-w-[500px]">
              {t('cta.intro')}
            </p>
            <div className="mt-12 space-y-5">
              <a href="mailto:info@mintpropertyadvisory.com.au" className="inline-flex items-center gap-4 text-[14px] text-[#C9C5B8] hover:text-[#5FE0C8] transition-colors group">
                <span className="w-10 h-10 rounded-full border border-white/10 grid place-items-center group-hover:border-[#5FE0C8]/40 transition-colors"><Mail className="w-4 h-4 text-[#5FE0C8]" /></span>
                info@mintpropertyadvisory.com.au
              </a>
            </div>
          </div>

          <div className="lg:col-span-6">
            <form onSubmit={submit} data-testid="contact-form" className="bg-[#0B1C18] border border-white/5 rounded-2xl p-8 md:p-10 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A7A7A]">{t('cta.formName')}</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-input-name" className="mt-2 w-full bg-transparent border-b border-white/10 py-3 text-[15px] outline-none focus:border-[#5FE0C8] transition-colors" placeholder={t('cta.formNamePh')} />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A7A7A]">{t('cta.formEmail')}</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-input-email" className="mt-2 w-full bg-transparent border-b border-white/10 py-3 text-[15px] outline-none focus:border-[#5FE0C8] transition-colors" placeholder={t('cta.formEmailPh')} />
                </div>
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A7A7A]">{t('cta.formCity')}</label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {CITY_CODES.map((code, i) => (
                    <button type="button" key={code} onClick={() => setForm({ ...form, city: code })}
                      data-testid={`contact-city-${code.toLowerCase()}`}
                      className={`px-4 py-2 rounded-full text-[13px] border transition-colors ${form.city === code ? 'bg-[#5FE0C8] text-[#07120F] border-[#5FE0C8]' : 'border-white/10 text-[#C9C5B8] hover:border-[#5FE0C8]/50'}`}>{cityLabels[i] || code}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A7A7A]">{t('cta.formBrief')}</label>
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} data-testid="contact-input-message" className="mt-2 w-full bg-transparent border border-white/10 rounded-lg p-4 text-[14px] outline-none focus:border-[#5FE0C8] transition-colors resize-none" placeholder={t('cta.formBriefPh')} />
              </div>
              <button type="submit" disabled={status === 'sending'} data-testid="contact-submit" className="btn-mint w-full py-4 rounded-full text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
                {status === 'sending' && (<><Loader2 className="w-4 h-4 animate-spin" /> {t('cta.btnSending')}</>)}
                {status === 'success' && (<><CheckCircle2 className="w-4 h-4" /> {t('cta.btnSuccess')}</>)}
                {status === 'error' && (<><AlertCircle className="w-4 h-4" /> {t('cta.btnError')}</>)}
                {status === 'idle' && (<>{t('cta.btnIdle')} <ArrowUpRight className="w-4 h-4" /></>)}
              </button>
              {status === 'error' && errorMsg && (
                <p className="text-[12px] text-red-400/90 leading-relaxed">{errorMsg}</p>
              )}
              <p className="text-[11px] text-[#7A7A7A] leading-relaxed">
                {t('cta.consent')}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
